import { GridSortItem } from "@mui/x-data-grid";
import { cloneDeep } from "lodash";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import Flex from "../../../../../components/Flex";
import MultiSpecialty from "../../../../../components/MultiSpecialty";
import { ScrollArea } from "../../../../../components/ScrollBar";
import ActionMenu from "../../../../../components/app/ActionMenu";
import CustomTitle from "../../../../../components/app/CustomTitle";
import DeleteModal, {
  useDeleteDisClosure,
} from "../../../../../components/app/DeleteModal";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";
import useDisclosure from "../../../../../hooks/use-disclosure";
import { useLoading } from "../../../../../hooks/use-loading";
import { useFormBuilderArchiveStatus } from "../../../../../queries/form-builder/use-form-builder-archive";
import { useFormBuilderByUuid } from "../../../../../queries/form-builder/use-form-builder-by-uuid";
import { useFormBuilderCreate } from "../../../../../queries/form-builder/use-form-builder-create";
import { useFormBuilderList } from "../../../../../queries/form-builder/use-form-builder-list";
import {
  DEFAULT_UUID_NAME_LIST,
  DEF_PAGINATION,
  DGT_Pagination,
  SnackbarInterface,
  applyDefaultColumnSettings,
  getUnNecessaryTransformData,
  transformMultiSelectFrontEndVal,
  getErrorSnackbar,
  getSuccessSnackbar,
} from "../../../../../utils/utils";
import FormBuilderHost from "./FormBuilderHost";
import PreviewFormDrawer from "./PreviewFormDrawer";
import CopyFormModal from "./copyFormModal";
import {
  FORM_ACTION_MENU_ITEMS,
  FORM_TYPE_MAPPER,
  FormActionValue,
  FormBuilderInitialValues,
} from "./form-builder-constant";
import { formatDate } from "../../../../../utils/date-time-utility";
import Text from "../../../../../components/Text";
import { useImportDefaultForms } from "../../../../../queries/form-builder/use-form-builder-default-import";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { getViewAs, getStoredRealm } from "@/utils/auth-storage";

type FormColumns = FormBuilderInitialValues & { modifiedDate?: string };

const DEFAULT_ERR_TOAST: SnackbarInterface = {
  type: "error",
  message: "Something went wrong",
};

/**
 * form builder table to list down and perform actions
 *
 * @author Rajkumar Rathod
 */
const FormBuilderTable = () => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const [curFormSchema, setCurFormSchema] = useState<any>();
  const [selectedForm, setSelectedForm] = useState<FormBuilderInitialValues>();
  const [isFormPreviewEdit, setIsFormPreviewEdit] = useState<boolean>(false);
  const navigate = useNavigate();
  const isProvider = getViewAs() === "provider";
  const realm = getStoredRealm();

  const previewFormDisclosure = useDisclosure(false);

  const {
    data,
    refetch: refetchTableLatestData,
    isLoading: isTableDataLoading,
    isFetching: isTableDataFetching,
  } = useFormBuilderList({
    pagination,
    gridSorting,
    xTENANTID: isProvider && realm ? realm : undefined,
  });
  const { mutateAsync: deleteForm } = useFormBuilderArchiveStatus();
  const deleteDisClosure = useDeleteDisClosure(false);
  const makeCopyDisclosureHost = useDisclosure(false);
  const [isLoading, setIsLoading] = useLoading();

  const showSnackbar = useSnackbar();

  const { mutateAsync: createForm } = useFormBuilderCreate();
  const { mutateAsync: importDefaultForms } = useImportDefaultForms();

  const {
    data: singleFormData,
    dataUpdatedAt,
    isLoading: isLoadingFormData,
    refetch,
    isRefetching,
    isFetching,
  } = useFormBuilderByUuid({
    customFormId: selectedForm?.uuid,
    xTENANTID: isProvider && realm ? realm : undefined,
  });

  const COLUMNS = applyDefaultColumnSettings<FormColumns>([
    {
      field: "formType",
      headerName: "Form Type",
      flex: 0.6,
      minWidth: 180,
      renderCell: ({ row }) => FORM_TYPE_MAPPER[row.formType ?? ""],
    },
    {
      field: "name",
      headerName: "Form Name",
      flex: 0.9,
      minWidth: 180,
      renderCell: ({ row }) => (
        <Text
          color={"primary"}
          sx={{ cursor: "pointer" }}
          onClick={() => handleActionClick("view", row)}
        >
          {row?.name}
        </Text>
      ),
    },
    {
      field: "specialities",
      flex: 0.7,
      headerName: "Specialties",
      minWidth: 160,
      sortable: false,
      renderCell: ({ value }) => (
        <MultiSpecialty specialties={value} width={280} />
      ),
    },
    {
      field: "createdBy",
      headerName: "Created By",
      flex: 0.6,
      minWidth: 100,
      renderCell: ({ value }) => (value && value?.trim() ? value : "-"),
    },
    {
      field: "updatedBy",
      headerName: "Updated Date",
      flex: 0.6,
      minWidth: 100,
      renderCell: ({ row }) => formatDate(row?.modifiedDate),
    },
    {
      field: "Action",
      width: 90,
      hideSortIcons: true,
      renderCell: ({ row }) => (
        <ActionMenu
          options={FORM_ACTION_MENU_ITEMS}
          onActionClick={async (value) => {
            const v = value as FormActionValue;
            handleActionClick(v, row);
          }}
        />
      ),
    },
  ]);

  useEffect(() => {
    if (singleFormData?.data) {
      fetchFormContent(singleFormData?.data).then((schema) => {
        setCurFormSchema(schema);
      });
    }
  }, [dataUpdatedAt]);

  async function handleActionClick(
    action: FormActionValue,
    selectedFormData?: any
  ) {
    setSelectedForm(selectedFormData);
    setCurFormSchema(undefined);

    if (action !== "archive") refetch();

    switch (action) {
      case "view":
        previewFormDisclosure.open();
        break;
      case "update":
        handlePreviewEdit();
        break;
      case "archive":
        deleteDisClosure.open(selectedFormData?.uuid);
        break;
      case "make_copy":
        refetch();
        makeCopyDisclosureHost.open();
        break;
      default:
        break;
    }
  }

  async function fetchFormContent(selectedTableData: any): Promise<any> {
    if (!selectedTableData?.content) return;
    setIsLoading(true);

    return await fetch(selectedTableData?.content)
      .then((response) => response?.json())
      .then((data) => {
        const transformedData = {
          uuid: selectedTableData.uuid,
          name: selectedTableData.name,
          formType: selectedTableData.formType,
          active: selectedTableData.active,
          specialities: transformMultiSelectFrontEndVal(
            DEFAULT_UUID_NAME_LIST,
            selectedTableData?.specialities
          ),
          // Object.keys(selectedTableData?.speciality ?? {}),
          components: data?.components,
        };

        return transformedData;
      })
      .catch(() => showSnackbar(DEFAULT_ERR_TOAST))
      .finally(() => setIsLoading(false));
  }

  function handlePreviewEdit() {
    setIsFormPreviewEdit((prev) => !prev);
    previewFormDisclosure.close();

    if (isFormPreviewEdit) refetchTableLatestData();
  }

  async function handleMakeCopy(formName: string) {
    if (!curFormSchema) return showSnackbar(DEFAULT_ERR_TOAST);

    // const speciality = specialtiesList?.data?.content?.find(
    //   (el) => el.uuid === curFormSchema.speciality
    // );

    const payload = getUnNecessaryTransformData(["specialities"], {
      ...curFormSchema,
      name: formName,
      components: undefined,
      content: JSON.stringify({
        components: curFormSchema?.components ?? [],
      }),
    });

    createForm({
      customForm: payload,
      xTENANTID: isProvider && realm ? realm : undefined,
    })
      .then((_res) => {
        showSnackbar({
          type: "success",
          message: "Form Copied Successfully",
        });
        refetchTableLatestData();

        makeCopyDisclosureHost.close();
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  }

  async function handleDelete<T extends string | undefined>(
    id: T
  ): Promise<any> {
    const res = await deleteForm({
      customFormId: id!,
      status: true,
      xTENANTID: isProvider && realm ? realm : undefined,
    });
    refetchTableLatestData();
    return res;
  }

  const isLoadingForm =
    isLoadingFormData || isFetching || isRefetching || isLoading;

  const viewAs = getViewAs();

  function handleNavigate() {
    navigate(
      viewAs === "provider"
        ? "/app/provider/library/form-builder-host"
        : "/app/library/form-builder-host"
    );
  }

  const handleImport = () => {
    importDefaultForms(realm ?? undefined)
      .then((res) => {
        refetchTableLatestData();
        showSnackbar(getSuccessSnackbar(res));
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  };

  return !isFormPreviewEdit ? (
    <>
      <Flex justifyContent={"space-between"} mb={"20px"}>
        <CustomTitle color={"black"}>Form Builder</CustomTitle>

        <Flex gap={2.5}>
          {viewAs === "provider" && (
            <StyledButtonV2
              shouldAddIcon
              onClick={handleImport}
              label="Import Default Forms"
            />
          )}
          <StyledButtonV2
            shouldAddIcon
            onClick={handleNavigate}
            label="Create New Form"
          />
        </Flex>
      </Flex>

      <CopyFormModal
        inputLabel="Form Name"
        isLoading={isLoadingForm}
        prefillName={selectedForm?.name}
        onConfirm={handleMakeCopy}
        disclosureHost={makeCopyDisclosureHost}
      />

      <DeleteModal
        w="380px"
        id={deleteDisClosure?.deleteId}
        open={deleteDisClosure.opened}
        onClose={deleteDisClosure.close}
        onConfirmation={handleDelete}
        content={"Do You want to delete form"}
      />

      <PreviewFormDrawer
        shouldEdit
        isLoading={isLoadingForm}
        disClosureHost={previewFormDisclosure}
        title={FORM_TYPE_MAPPER[curFormSchema?.formType] ?? "Preview Form"}
        schema={curFormSchema}
        onEdit={handlePreviewEdit}
      />

      <ScrollArea height={"calc(100vh - 11.5rem)"}>
        <CustomDataGridV2
          loading={isTableDataLoading || isTableDataFetching}
          rows={data?.data?.content ?? []}
          onSortModelChange={setGridSorting}
          getRowId={(row) => row?.uuid}
          columns={COLUMNS}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          height="100%"
          rowCount={data?.data?.page?.totalElements}
          disableRowSelectionOnClick
        />
      </ScrollArea>
    </>
  ) : (
    <FormBuilderHost
      onClose={handlePreviewEdit}
      preFillSchema={cloneDeep(curFormSchema)}
      isLoading={isLoadingForm}
    />
  );
};

export default FormBuilderTable;
